import React, { useState, useContext } from 'react'
import { useTranslation } from 'react-i18next'
import axios from 'axios'
import {
  Form,
  FormGroup,
  FormControl,
  Button,
  Row,
  Col,
  Modal,
} from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faAngleRight,
  faAngleDown,
  faAngleUp,
  faTrash,
  faExpand,
} from '@fortawesome/free-solid-svg-icons'
import { useNavigate } from 'react-router-dom'

import { useLanguage } from '../contexts/LanguageContext'
import { useAuth, AuthContext } from '../contexts/AuthContext'
import config from '../config/config'
import { LOGIN_PATH } from '../config/routes'
import PhotoExif from './PhotoExif'
import { useWebsite, setFlashMessage } from '../contexts/WebsiteContext'

const ManageItemPhoto = ({ photo, handleUpdatePhotoRanking }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { logout } = useContext(AuthContext)
  const { activeLanguage } = useLanguage()
  const { token } = useAuth()

  const [isDeleted, setIsDeleted] = useState(false)

  const [showDetails, setShowDetails] = useState(false)
  const [showFullScreen, setShowFullScreen] = useState(false)
  const [icon, setIcon] = useState(faAngleRight)
  const [labels, setLabels] = useState({
    labelFr: photo.labelFr,
    labelEn: photo.labelEn,
    labelEs: photo.labelEs,
  })
  const [showConfirmationModal, setShowConfirmationModal] = useState(false)

  const toggleDetails = () => {
    setShowDetails(!showDetails)
    setIcon(showDetails ? faAngleRight : faAngleDown)
  }

  const handleLabelChange = (e) => {
    const { name, value } = e.target
    setLabels({ ...labels, [name]: value })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    try {
      const data = {
        labelFr: labels.labelFr,
        labelEn: labels.labelEn,
        labelEs: labels.labelEs,
      }
      await axios.put(`${config.apiUrl}photo/${photo.id}`, data, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      setFlashMessage(t('SUCCESS_UPDATED_MESSAGE'), 'success')
    } catch (error) {
      if (error.response && error.response.status === 401) {
        logout()
        navigate(LOGIN_PATH)
      } else if (error.response) {
        setFlashMessage(t('ERROR_MESSAGE'), 'error')
        console.log('Erreur lors du PUT photo', photo.id)
      }
    }
  }

  const toggleFullScreen = () => {
    setShowFullScreen(!showFullScreen)
  }

  const handleTrashClick = () => {
    setShowConfirmationModal(true)
  }

  const handleDelete = async () => {
    try {
      await axios.delete(`${config.apiUrl}photo/${photo.id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      setIsDeleted(true)

      setFlashMessage(t('SUCCESS_DELETED_MESSAGE'), 'success')
    } catch (error) {
      if (error.response && error.response.status === 401) {
        logout()
        navigate(LOGIN_PATH)
      } else if (error.response) {
        setFlashMessage(t('ERROR_MESSAGE'), 'error')
        console.log('Erreur lors du DELETE photo', photo.id)
      }
    } finally {
      setShowConfirmationModal(false)
    }
  }

  const handleRankingUpdate = async (direction) => {
    try {
      await axios.put(
        `${config.apiUrl}photo/${photo.id}`,
        { ranking: direction },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )

      handleUpdatePhotoRanking()

      setFlashMessage(t('SUCCESS_UPDATED_MESSAGE'), 'success')
    } catch (error) {
      if (error.response && error.response.status === 401) {
        logout()
        navigate(LOGIN_PATH)
      } else if (error.response) {
        setFlashMessage(t('ERROR_MESSAGE'), 'error')
        console.log('Erreur lors du PUT photo', photo.id)
      }
    }
  }

  const getTranslatedLabel = () => {
    switch (activeLanguage) {
      case 'fr':
        return labels.labelFr
      case 'en':
        return labels.labelEn
      case 'es':
        return labels.labelEs
      default:
        return labels.labelFr
    }
  }

  // Rend le composant seulement si la photo n'a pas été supprimée avec succès
  if (isDeleted) {
    return null
  }

  return (
    <div className="photo-container">
      <div className="text-center">
        <img
          src={`${config.photoUrlMedium}${photo.id}.jpg`}
          alt={getTranslatedLabel()}
        />
        <div className="photo-label">
          <strong>
            <i>{getTranslatedLabel()}</i>
          </strong>
          <Button
            variant="primary"
            className={'expand-button'}
            style={{ marginRight: '10px' }}
            onClick={toggleDetails}
          >
            {t('DETAILS')} <FontAwesomeIcon icon={icon} />
          </Button>
          <Button
            variant="primary"
            style={{ marginRight: '10px' }}
            onClick={() => handleTrashClick(photo.id)}
          >
            <FontAwesomeIcon icon={faTrash} />
          </Button>
          <Button
            variant="primary"
            style={{ marginRight: '10px' }}
            onClick={() => handleRankingUpdate('up')}
          >
            <FontAwesomeIcon icon={faAngleUp} />
          </Button>
          <Button
            variant="primary"
            style={{ marginRight: '10px' }}
            onClick={() => handleRankingUpdate('down')}
          >
            <FontAwesomeIcon icon={faAngleDown} />
          </Button>
          {photo.watermarkUuid && (
            <button className="btn btn-primary" onClick={toggleFullScreen}>
              <FontAwesomeIcon icon={faExpand} />
            </button>
          )}
        </div>
      </div>
      {showDetails && (
        <div className="green-frame">
          <PhotoExif photo={photo} t={t} />
          <Form onSubmit={handleSubmit}>
            <Row className="align-items-center margin-bottom-20">
              <Col>
                <FormGroup>
                  <Form.Label htmlFor="labelFr">{t('Label Fr')}</Form.Label>
                  <FormControl
                    type="text"
                    id="labelFr"
                    name="labelFr"
                    value={labels.labelFr}
                    onChange={handleLabelChange}
                  />
                </FormGroup>
              </Col>
              <Col>
                <FormGroup>
                  <Form.Label htmlFor="labelEn">{t('Label En')}</Form.Label>
                  <FormControl
                    type="text"
                    id="labelEn"
                    name="labelEn"
                    value={labels.labelEn}
                    onChange={handleLabelChange}
                  />
                </FormGroup>
              </Col>
              <Col>
                <FormGroup>
                  <Form.Label htmlFor="labelEs">{t('Label Es')}</Form.Label>
                  <FormControl
                    type="text"
                    id="labelEs"
                    name="labelEs"
                    value={labels.labelEs}
                    onChange={handleLabelChange}
                  />
                </FormGroup>
              </Col>
              <Col className="text-center">
                <Button
                  type="submit"
                  variant="primary"
                  style={{ marginTop: '20px', marginRight: '10px' }}
                >
                  {t('UPDATE')}
                </Button>
              </Col>
            </Row>
          </Form>
        </div>
      )}

      {/* Display watermarked photo */}
      {photo.watermarkUuid && showFullScreen && (
        <div className="full-screen-overlay" onClick={toggleFullScreen}>
          <img
            src={`${config.photoUrlWatermark}${photo.watermarkUuid}.jpg`}
            alt={getTranslatedLabel()}
            className="full-screen-image"
          />
        </div>
      )}

      {/* Modal for confirmation */}
      <Modal
        show={showConfirmationModal}
        onHide={() => setShowConfirmationModal(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>{t('CONFIRM_DELETE')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{t('CONFIRM_DELETE_MESSAGE')}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowConfirmationModal(false)}
          >
            {t('CANCEL')}
          </Button>
          <Button variant="primary" onClick={handleDelete}>
            {t('CONFIRM')}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}

export default ManageItemPhoto
